// pages/DemoLandingPage.tsx
/**
 * Demo landing page with all buildings and QR codes
 * Fetches from API to show real database content
 */
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ExternalLink, Copy, CheckCircle, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getBuildings, type Building } from '../lib/api';
import { useToast } from '../components/ui/Toast';

export function DemoLandingPage() {
  const { showToast } = useToast();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchBuildings() {
      setLoading(true);
      const { data, error } = await getBuildings();
      if (error) {
        setError(error);
      } else if (data) {
        setBuildings(data.buildings);
      }
      setLoading(false);
    }
    fetchBuildings();
  }, []);

  return (
    <div className="min-h-screen bg-bg-base">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Beaux Properties" className="h-10" />
              <p className="text-body-sm text-text-muted">QR Code Platform Prototype</p>
            </div>
            <Link
              to="/admin"
              className="flex items-center gap-2 px-4 py-2.5 bg-text text-text-inverse rounded-lg hover:bg-text/90 transition-colors text-body-sm font-medium"
            >
              Admin Dashboard
              <ExternalLink size={16} />
            </Link>
          </div>
        </div>
      </header>

      {/* Intro Section */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-primary-subtle border border-primary/10 rounded-lg p-6 mb-8">
          <h2 className="font-display text-display-sm text-text mb-2">Demo Instructions</h2>
          <p className="text-body-md text-text-secondary mb-4">
            This prototype demonstrates the QR code tenant experience. Click any building below or scan its QR code to see the mobile-first landing page prospective tenants would see.
          </p>
          <div className="flex flex-wrap gap-6 text-body-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-success rounded-full"></span>
              <span className="text-text-secondary">Tenant Flow: Click a building card</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-warning rounded-full"></span>
              <span className="text-text-secondary">Admin Flow: Click "Admin Dashboard"</span>
            </div>
          </div>
        </div>

        {/* Buildings Grid */}
        <h2 className="font-display text-display-sm text-text mb-4">Select a Building to Test</h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-gray-600">Loading buildings...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-800">Failed to load buildings: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : buildings.length === 0 ? (
          <div className="bg-gray-100 rounded-xl p-6 text-center">
            <p className="text-gray-600">No buildings found in database.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {buildings.map(building => (
              <BuildingDemoCard key={building.id} building={building} showToast={showToast} />
            ))}
          </div>
        )}
      </section>

      {/* Quick Links */}
      <section className="max-w-6xl mx-auto px-4 py-8 border-t border-border">
        <h3 className="font-display text-display-sm text-text mb-4">Quick Access</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin" className="px-4 py-2 bg-bg-subtle rounded-lg hover:bg-border-subtle text-body-sm text-text-secondary transition-colors">
            Admin Dashboard
          </Link>
          <Link to="/admin/analytics" className="px-4 py-2 bg-bg-subtle rounded-lg hover:bg-border-subtle text-body-sm text-text-secondary transition-colors">
            Analytics Overview
          </Link>
          <Link to="/admin/users" className="px-4 py-2 bg-bg-subtle rounded-lg hover:bg-border-subtle text-body-sm text-text-secondary transition-colors">
            Users
          </Link>
          <Link to="/admin/settings" className="px-4 py-2 bg-bg-subtle rounded-lg hover:bg-border-subtle text-body-sm text-text-secondary transition-colors">
            Settings
          </Link>
        </div>
      </section>

      {/* Stats Summary */}
      {!loading && buildings.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-8 border-t border-border">
          <h3 className="font-display text-display-sm text-text mb-4">Portfolio Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-surface rounded-lg border border-border p-4 text-center">
              <p className="text-price-lg text-primary">{buildings.length}</p>
              <p className="text-body-sm text-text-muted">Buildings</p>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4 text-center">
              <p className="text-price-lg text-primary">22</p>
              <p className="text-body-sm text-text-muted">Available Units</p>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4 text-center">
              <p className="text-price-lg text-primary">$1,650</p>
              <p className="text-body-sm text-text-muted">Starting From</p>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4 text-center">
              <p className="text-price-lg text-primary">
                {buildings.length > 0
                  ? Math.round(buildings.reduce((sum, b) => sum + (b.walk_score || 0), 0) / buildings.length)
                  : 0}
              </p>
              <p className="text-body-sm text-text-muted">Avg Walk Score</p>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-surface border-t border-border mt-8">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-text-muted text-body-sm">
          Prototype by Fp Digital &bull; January 2025
        </div>
      </footer>
    </div>
  );
}

interface BuildingDemoCardProps {
  building: Building;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
}

function BuildingDemoCard({ building, showToast }: BuildingDemoCardProps) {
  const [copied, setCopied] = useState(false);
  const demoUrl = `${window.location.origin}/b/${building.slug}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(demoUrl);
      setCopied(true);
      showToast('URL copied to clipboard', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Failed to copy URL', 'error');
    }
  };

  // Extract neighborhood from address or use city
  const neighborhood = building.neighborhood_description?.split('.')[0] || building.city;

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden hover:border-border-strong hover:shadow-md transition-all">
      {/* Building Image - Opens in new tab */}
      <a href={`/b/${building.slug}`} target="_blank" rel="noopener noreferrer">
        <div className="aspect-video bg-bg-subtle relative overflow-hidden">
          {building.hero_image_url ? (
            <img
              src={building.hero_image_url}
              alt={building.name}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-bg-subtle">
              <span className="text-text-muted">No image</span>
            </div>
          )}
        </div>
      </a>

      {/* Card Content */}
      <div className="p-4">
        {/* Building Name */}
        <h3 className="font-display text-display-sm text-text mb-1">{building.name}</h3>
        <p className="text-body-sm text-text-secondary mb-4">{neighborhood}</p>

        <div className="flex gap-4">
          {/* QR Code */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-white border border-border rounded-lg p-1">
              <QRCodeSVG value={demoUrl} size={72} level="M" />
            </div>
          </div>

          {/* Building Info */}
          <div className="flex-1 min-w-0">
            <p className="text-body-sm font-medium text-primary mb-1">
              {building.address}
            </p>
            <p className="text-label-sm text-text-muted mb-2">
              {building.city}, {building.postal_code}
            </p>
            <div className="flex gap-2">
              {building.walk_score && (
                <span className="text-xs bg-success-subtle text-success-text px-2 py-0.5 rounded-full font-medium">
                  Walk {building.walk_score}
                </span>
              )}
              {building.transit_score && (
                <span className="text-xs bg-info-subtle text-info-text px-2 py-0.5 rounded-full font-medium">
                  Transit {building.transit_score}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <a
            href={`/b/${building.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center py-2.5 bg-primary text-text-inverse rounded-lg text-body-sm font-medium hover:bg-primary-hover transition-colors"
          >
            View as Tenant
          </a>
          <button
            onClick={copyUrl}
            className="flex items-center justify-center gap-1 px-4 py-2.5 border border-border rounded-lg text-body-sm hover:bg-bg-subtle transition-colors"
            title="Copy URL"
          >
            {copied ? (
              <CheckCircle size={16} className="text-success" />
            ) : (
              <Copy size={16} className="text-text-muted" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
